import React, { useCallback, useEffect, useState } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import { Node, mergeAttributes } from '@tiptap/core';
import StarterKit from '@tiptap/starter-kit';
import { Image } from '@tiptap/extension-image';
import { Link } from '@tiptap/extension-link';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';
import { Youtube } from '@tiptap/extension-youtube';
import { Placeholder } from '@tiptap/extension-placeholder';
import { TextAlign } from '@tiptap/extension-text-align';
import { Underline } from '@tiptap/extension-underline';
import { Highlight } from '@tiptap/extension-highlight';
import { TextStyle } from '@tiptap/extension-text-style';
import { Color } from '@tiptap/extension-color';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { 
  Bold, Italic, Underline as UnderlineIcon, Strikethrough,
  List, ListOrdered, Quote, Code, Heading1, Heading2, Heading3,
  Link as LinkIcon, Image as ImageIcon, Youtube as YoutubeIcon,
  Table as TableIcon, AlignLeft, AlignCenter, AlignRight, AlignJustify,
  Highlighter, Undo, Redo, Minus, Plus, X, Palette
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../ui/popover';

// Custom Iframe node — used for non-YouTube providers (Dadan, etc.)
// TipTap's YouTube extension only handles YouTube; this lets us drop in any
// allowlisted iframe src (host filtering happens on the backend for notes/pages).
const IframeNode = Node.create({
  name: 'iframe',
  group: 'block',
  atom: true,
  selectable: true,
  addAttributes() {
    return {
      src: { default: null },
      width: { default: '640' },
      height: { default: '360' },
      frameborder: { default: '0' },
      allow: { default: 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen' },
      allowfullscreen: { default: true },
      referrerpolicy: { default: 'strict-origin-when-cross-origin' },
      title: { default: 'Embedded video' },
    };
  },
  parseHTML() {
    return [{ tag: 'iframe' }];
  },
  renderHTML({ HTMLAttributes }) {
    return ['div', { class: 'video-embed-wrapper my-4' }, ['iframe', mergeAttributes(HTMLAttributes, { class: 'mx-auto max-w-full' })]];
  },
});

// Extract the src="..." attribute from a raw <iframe ...> HTML snippet.
const _extractIframeSrc = (html) => {
  const m = /src\s*=\s*["']([^"']+)["']/i.exec(html || '');
  return m ? m[1].trim() : null;
};

// Detect provider from a URL. Returns 'youtube' | 'dadan' | null.
const _DADAN_CUSTOM_DOMAINS = new Set(['video.helmtorq.com']);
const _detectProvider = (url) => {
  try {
    const host = new URL(url).hostname.toLowerCase();
    if (host === 'youtu.be' || host === 'youtube.com' || host.endsWith('.youtube.com')) return 'youtube';
    if (host === 'dadan.io' || host.endsWith('.dadan.io') || _DADAN_CUSTOM_DOMAINS.has(host)) return 'dadan';
    return null;
  } catch (_e) {
    return null;
  }
};

// Menu button component
const MenuButton = ({ onClick, isActive, disabled, children, title }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={disabled}
    title={title}
    className={`p-2 rounded hover:bg-gray-100 transition-colors ${
      isActive ? 'bg-[#14B8A6]/20 text-[#14B8A6]' : 'text-gray-600'
    } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
  >
    {children}
  </button>
);

// Color picker
const ColorPicker = ({ editor }) => {
  const colors = [
    '#000000', '#374151', '#DC2626', '#EA580C', '#CA8A04', 
    '#16A34A', '#0891B2', '#2563EB', '#7C3AED', '#DB2777'
  ];

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="p-2 rounded hover:bg-gray-100 transition-colors text-gray-600"
          title="Text Color"
        >
          <Palette className="w-4 h-4" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-2">
        <div className="flex flex-wrap gap-1 max-w-[140px]">
          {colors.map((color) => (
            <button
              key={color}
              type="button"
              onClick={() => editor.chain().focus().setColor(color).run()}
              className="w-6 h-6 rounded border border-gray-200 hover:scale-110 transition-transform"
              style={{ backgroundColor: color }}
              title={color}
            />
          ))}
          <button
            type="button"
            onClick={() => editor.chain().focus().unsetColor().run()}
            className="w-6 h-6 rounded border border-gray-200 hover:bg-gray-100 flex items-center justify-center"
            title="Remove color"
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

// Main Editor Component
const RichTextEditor = ({ 
  content = '', 
  onChange, 
  onImageSelect,
  placeholder = 'Start writing your content...',
  editable = true,
  onEditorReady,
}) => {
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [videoDialogOpen, setVideoDialogOpen] = useState(false);
  const [videoUrl, setVideoUrl] = useState('');
  const [videoError, setVideoError] = useState('');

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3],
        },
        // Disable built-in link and underline to use custom configurations
        link: false,
        underline: false,
      }),
      Image.configure({
        inline: true,
        allowBase64: true,
      }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-[#14B8A6] underline hover:text-[#0F766E]',
        },
      }),
      Table.configure({
        resizable: true,
        HTMLAttributes: {
          class: 'border-collapse border border-gray-300 w-full',
        },
      }),
      TableRow,
      TableCell.configure({
        HTMLAttributes: {
          class: 'border border-gray-300 p-2',
        },
      }),
      TableHeader.configure({
        HTMLAttributes: {
          class: 'border border-gray-300 p-2 bg-gray-100 font-semibold',
        },
      }),
      Youtube.configure({
        width: 640,
        height: 360,
        HTMLAttributes: {
          class: 'mx-auto my-4',
        },
      }),
      IframeNode,
      Placeholder.configure({
        placeholder,
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      Underline,
      Highlight.configure({
        multicolor: true,
      }),
      TextStyle,
      Color,
    ],
    content,
    editable,
    onUpdate: ({ editor }) => {
      onChange?.(editor.getHTML());
    },
  });

  // Expose the underlying TipTap editor to the parent so callers can run
  // programmatic commands (insert content at cursor, etc.). Fired once
  // ready and again on any re-init.
  useEffect(() => {
    if (editor && onEditorReady) {
      onEditorReady(editor);
    }
  }, [editor, onEditorReady]);

  // Link handlers
  const handleSetLink = useCallback(() => {
    if (!linkUrl) {
      editor.chain().focus().unsetLink().run();
    } else {
      editor.chain().focus().extendMarkRange('link').setLink({ href: linkUrl }).run();
    }
    setLinkDialogOpen(false);
    setLinkUrl('');
  }, [editor, linkUrl]);

  const openLinkDialog = useCallback(() => {
    const previousUrl = editor.getAttributes('link').href;
    setLinkUrl(previousUrl || '');
    setLinkDialogOpen(true);
  }, [editor]);

  // Video handler — accepts YouTube URLs, Dadan URLs, or a full <iframe> HTML snippet.
  const handleAddVideo = useCallback(() => {
    const raw = (videoUrl || '').trim();
    if (!raw) return;

    // If the input is an <iframe ...> snippet, pull out its src.
    let src = raw;
    if (/^<iframe/i.test(raw) || /src=/i.test(raw)) {
      const extracted = _extractIframeSrc(raw);
      if (!extracted) {
        setVideoError('Could not read the iframe src. Paste a direct URL or the full <iframe> tag.');
        return;
      }
      src = extracted;
    }

    // Validate provider (matches backend allowlist for notes/pages).
    const provider = _detectProvider(src);
    if (!provider) {
      setVideoError('Only YouTube and Dadan videos are supported.');
      return;
    }

    if (provider === 'youtube') {
      editor.commands.setYoutubeVideo({ src });
    } else {
      // Insert the iframe node directly — renders the pasted Dadan/YouTube embed.
      editor
        .chain()
        .focus()
        .insertContent({
          type: 'iframe',
          attrs: {
            src,
            width: '640',
            height: '360',
            frameborder: '0',
            allowfullscreen: true,
            title: 'Embedded video',
          },
        })
        .run();
    }

    setVideoDialogOpen(false);
    setVideoUrl('');
    setVideoError('');
  }, [editor, videoUrl]);

  // Table handlers
  const insertTable = useCallback(() => {
    editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
  }, [editor]);

  // Image handler (uses Picture Manager)
  const handleImageClick = useCallback(() => {
    if (onImageSelect) {
      onImageSelect((imageUrl) => {
        if (imageUrl) {
          editor.chain().focus().setImage({ src: imageUrl }).run();
        }
      });
    }
  }, [editor, onImageSelect]);

  if (!editor) {
    return null;
  }

  return (
    <div className="border border-gray-300 rounded-lg overflow-hidden bg-white">
      {/* Toolbar */}
      {editable && (
        <div className="border-b border-gray-200 p-2 bg-gray-50 flex flex-wrap gap-1">
          {/* Undo/Redo */}
          <MenuButton
            onClick={() => editor.chain().focus().undo().run()}
            disabled={!editor.can().undo()}
            title="Undo"
          >
            <Undo className="w-4 h-4" />
          </MenuButton>
          <MenuButton
            onClick={() => editor.chain().focus().redo().run()}
            disabled={!editor.can().redo()}
            title="Redo"
          >
            <Redo className="w-4 h-4" />
          </MenuButton>

          <div className="w-px h-6 bg-gray-300 mx-1 self-center" />

          {/* Headings */}
          <MenuButton
            onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
            isActive={editor.isActive('heading', { level: 1 })}
            title="Heading 1"
          >
            <Heading1 className="w-4 h-4" />
          </MenuButton>
          <MenuButton
            onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
            isActive={editor.isActive('heading', { level: 2 })}
            title="Heading 2"
          >
            <Heading2 className="w-4 h-4" />
          </MenuButton>
          <MenuButton
            onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
            isActive={editor.isActive('heading', { level: 3 })}
            title="Heading 3"
          >
            <Heading3 className="w-4 h-4" />
          </MenuButton>

          <div className="w-px h-6 bg-gray-300 mx-1 self-center" />

          {/* Text formatting */}
          <MenuButton
            onClick={() => editor.chain().focus().toggleBold().run()}
            isActive={editor.isActive('bold')}
            title="Bold"
          >
            <Bold className="w-4 h-4" />
          </MenuButton>
          <MenuButton
            onClick={() => editor.chain().focus().toggleItalic().run()}
            isActive={editor.isActive('italic')}
            title="Italic"
          >
            <Italic className="w-4 h-4" />
          </MenuButton>
          <MenuButton
            onClick={() => editor.chain().focus().toggleUnderline().run()}
            isActive={editor.isActive('underline')}
            title="Underline"
          >
            <UnderlineIcon className="w-4 h-4" />
          </MenuButton>
          <MenuButton
            onClick={() => editor.chain().focus().toggleStrike().run()}
            isActive={editor.isActive('strike')}
            title="Strikethrough"
          >
            <Strikethrough className="w-4 h-4" />
          </MenuButton>
          <MenuButton
            onClick={() => editor.chain().focus().toggleHighlight().run()}
            isActive={editor.isActive('highlight')}
            title="Highlight"
          >
            <Highlighter className="w-4 h-4" />
          </MenuButton>
          <ColorPicker editor={editor} />

          <div className="w-px h-6 bg-gray-300 mx-1 self-center" />

          {/* Alignment */}
          <MenuButton
            onClick={() => editor.chain().focus().setTextAlign('left').run()}
            isActive={editor.isActive({ textAlign: 'left' })}
            title="Align Left"
          >
            <AlignLeft className="w-4 h-4" />
          </MenuButton>
          <MenuButton
            onClick={() => editor.chain().focus().setTextAlign('center').run()}
            isActive={editor.isActive({ textAlign: 'center' })}
            title="Align Center"
          >
            <AlignCenter className="w-4 h-4" />
          </MenuButton>
          <MenuButton
            onClick={() => editor.chain().focus().setTextAlign('right').run()}
            isActive={editor.isActive({ textAlign: 'right' })}
            title="Align Right"
          >
            <AlignRight className="w-4 h-4" />
          </MenuButton>
          <MenuButton
            onClick={() => editor.chain().focus().setTextAlign('justify').run()}
            isActive={editor.isActive({ textAlign: 'justify' })}
            title="Justify"
          >
            <AlignJustify className="w-4 h-4" />
          </MenuButton>

          <div className="w-px h-6 bg-gray-300 mx-1 self-center" />

          {/* Lists */}
          <MenuButton
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            isActive={editor.isActive('bulletList')}
            title="Bullet List"
          >
            <List className="w-4 h-4" />
          </MenuButton>
          <MenuButton
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            isActive={editor.isActive('orderedList')}
            title="Numbered List"
          >
            <ListOrdered className="w-4 h-4" />
          </MenuButton>
          <MenuButton
            onClick={() => editor.chain().focus().toggleBlockquote().run()}
            isActive={editor.isActive('blockquote')}
            title="Quote"
          >
            <Quote className="w-4 h-4" />
          </MenuButton>
          <MenuButton
            onClick={() => editor.chain().focus().toggleCodeBlock().run()}
            isActive={editor.isActive('codeBlock')}
            title="Code Block"
          >
            <Code className="w-4 h-4" />
          </MenuButton>

          <div className="w-px h-6 bg-gray-300 mx-1 self-center" />

          {/* Insert */}
          <MenuButton onClick={openLinkDialog} isActive={editor.isActive('link')} title="Link">
            <LinkIcon className="w-4 h-4" />
          </MenuButton>
          <MenuButton onClick={handleImageClick} title="Insert Image">
            <ImageIcon className="w-4 h-4" />
          </MenuButton>
          <MenuButton onClick={() => { setVideoError(''); setVideoDialogOpen(true); }} title="Embed Video (YouTube or Dadan)">
            <YoutubeIcon className="w-4 h-4" />
          </MenuButton>
          <MenuButton onClick={insertTable} title="Insert Table">
            <TableIcon className="w-4 h-4" />
          </MenuButton>

          {/* Table controls (show when in table) */}
          {editor.isActive('table') && (
            <>
              <div className="w-px h-6 bg-gray-300 mx-1 self-center" />
              <MenuButton
                onClick={() => editor.chain().focus().addColumnAfter().run()}
                title="Add Column"
              >
                <Plus className="w-4 h-4" />
              </MenuButton>
              <MenuButton
                onClick={() => editor.chain().focus().deleteColumn().run()}
                title="Delete Column"
              >
                <Minus className="w-4 h-4" />
              </MenuButton>
              <MenuButton
                onClick={() => editor.chain().focus().addRowAfter().run()}
                title="Add Row"
              >
                <Plus className="w-4 h-4 rotate-90" />
              </MenuButton>
              <MenuButton
                onClick={() => editor.chain().focus().deleteRow().run()}
                title="Delete Row"
              >
                <Minus className="w-4 h-4 rotate-90" />
              </MenuButton>
              <MenuButton
                onClick={() => editor.chain().focus().deleteTable().run()}
                title="Delete Table"
              >
                <X className="w-4 h-4 text-red-500" />
              </MenuButton>
            </>
          )}
        </div>
      )}

      {/* Editor Content */}
      <EditorContent 
        editor={editor} 
        className="prose prose-sm max-w-none p-4 min-h-[300px] focus:outline-none
          [&_.ProseMirror]:outline-none
          [&_.ProseMirror_p.is-editor-empty:first-child::before]:text-gray-400
          [&_.ProseMirror_p.is-editor-empty:first-child::before]:content-[attr(data-placeholder)]
          [&_.ProseMirror_p.is-editor-empty:first-child::before]:float-left
          [&_.ProseMirror_p.is-editor-empty:first-child::before]:h-0
          [&_.ProseMirror_p.is-editor-empty:first-child::before]:pointer-events-none
          [&_.ProseMirror_table]:w-full
          [&_.ProseMirror_table]:border-collapse
          [&_.ProseMirror_td]:border
          [&_.ProseMirror_td]:border-gray-300
          [&_.ProseMirror_td]:p-2
          [&_.ProseMirror_th]:border
          [&_.ProseMirror_th]:border-gray-300
          [&_.ProseMirror_th]:p-2
          [&_.ProseMirror_th]:bg-gray-100
          [&_.ProseMirror_img]:max-w-full
          [&_.ProseMirror_img]:rounded
          [&_.ProseMirror_blockquote]:border-l-4
          [&_.ProseMirror_blockquote]:border-gray-300
          [&_.ProseMirror_blockquote]:pl-4
          [&_.ProseMirror_blockquote]:italic
          [&_.ProseMirror_pre]:bg-gray-900
          [&_.ProseMirror_pre]:text-white
          [&_.ProseMirror_pre]:p-4
          [&_.ProseMirror_pre]:rounded
          [&_.ProseMirror_code]:bg-gray-100
          [&_.ProseMirror_code]:px-1
          [&_.ProseMirror_code]:rounded
        "
      />

      {/* Link Dialog */}
      <Dialog open={linkDialogOpen} onOpenChange={setLinkDialogOpen}>
        <DialogContent className="sm:max-w-md" aria-describedby="link-dialog-description">
          <DialogHeader>
            <DialogTitle>Insert Link</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <Input
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              placeholder="https://example.com"
              onKeyDown={(e) => e.key === 'Enter' && handleSetLink()}
              aria-label="Link URL"
            />
            <p id="link-dialog-description" className="sr-only">
              Enter the URL for the link you want to insert
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLinkDialogOpen(false)}>Cancel</Button>
            <Button onClick={handleSetLink}>
              {linkUrl ? 'Set Link' : 'Remove Link'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Video Embed Dialog (YouTube or Dadan) */}
      <Dialog open={videoDialogOpen} onOpenChange={(open) => { setVideoDialogOpen(open); if (!open) setVideoError(''); }}>
        <DialogContent className="sm:max-w-md" aria-describedby="video-dialog-description">
          <DialogHeader>
            <DialogTitle>Embed Video (YouTube or Dadan)</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Input
              value={videoUrl}
              onChange={(e) => { setVideoUrl(e.target.value); if (videoError) setVideoError(''); }}
              placeholder="Paste a YouTube URL, Dadan share URL, or full <iframe> embed code"
              onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && handleAddVideo()}
              aria-label="Video URL or iframe embed"
              data-testid="video-embed-input"
            />
            <p id="video-dialog-description" className="text-xs text-gray-500">
              Supported: YouTube (youtube.com, youtu.be) and Dadan (dadan.io, video.helmtorq.com). You can paste a direct URL, a share link, or the full <code className="px-1 bg-gray-100 rounded">&lt;iframe&gt;</code> embed code.
            </p>
            {videoError && (
              <p className="text-sm text-red-600" data-testid="video-embed-error">{videoError}</p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setVideoDialogOpen(false)} data-testid="video-embed-cancel">Cancel</Button>
            <Button onClick={handleAddVideo} disabled={!videoUrl.trim()} data-testid="video-embed-submit">
              Embed Video
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RichTextEditor;
